import { useState } from 'react'
import type { Company } from '@/entities/company'
import { writeThroughErrorText } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'
import { useEditCompany } from '../api/edit'
import { EditCompanySchema } from '../model/schema'

interface EditCompanyDialogProps {
  company: Company
  open: boolean
  onClose: () => void
  onSaved?: (company: Company) => void
}

// Профиль компании: имя. Slug — проекция хаба (read-only, правка не пробрасывается,
// глушится бэкендом), показываем disabled. Реквизиты и контакты правятся отдельными
// диалогами со своими кнопками во вкладке «Реквизиты».
export function EditCompanyDialog({ company, open, onClose, onSaved }: EditCompanyDialogProps) {
  const edit = useEditCompany(company.id)
  const [name, setName] = useState(company.name)
  const [errors, setErrors] = useState<Record<'name', string | undefined>>({
    name: undefined,
  })

  function submit() {
    // Валидируем имя (slug неизменный — read-only проекция хаба, в PATCH не шлём).
    const parsed = EditCompanySchema.safeParse({ name: name.trim() })
    if (!parsed.success) {
      setErrors({ name: parsed.error.issues[0]?.message ?? 'Проверьте название' })
      return
    }
    setErrors({ name: undefined })
    edit.mutate(
      { name: parsed.data.name },
      {
        onSuccess: (updated) => {
          toast.success('Сохранено')
          onSaved?.(updated)
          onClose()
        },
        onError: (e) =>
          toast.error(
            writeThroughErrorText(e) ?? (e instanceof Error ? e.message : 'Не удалось сохранить'),
          ),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Компания"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={edit.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={edit.isPending}>
            {edit.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Название" {...(errors.name ? { error: errors.name } : {})}>
          {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Slug" hint="Адрес компании задаётся в хабе и здесь не меняется.">
          {(props) => <Input {...props} value={company.slug} disabled readOnly />}
        </Field>
      </div>
    </Dialog>
  )
}
